import React, { useCallback, useEffect, useState } from 'react'
import Resx from '@/components/Resx'
import { Main, MainTop, MainTopLeft, MainTopTitle, MainBody } from '@/components/Layout/Layout.Main'
import { WhiteBox } from '@/components/Layout/Layout.Box'
import { actionCreators } from '@/store/Messages'
import { BsrsGrid, actionButtonsColumnProps, DateCell, DateTimeCell, DateFilterCell, DateTimeFilterCell } from '@/components/BsrsGrid'
import { GridColumn as Column } from '@progress/kendo-react-grid'
import { Link, useHistory } from 'react-router-dom'
import context from '@/context'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { actionCreators as dialogCreators } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import Icon from '@/components/Icon'
import { MessagesTabs } from '@/enums/MessagesTabs'
import NameColumn from '@/components/BsrsGrid/BsrsGrid.NameColumn'
import PermissionRequired from '@/components/auth/PermissionRequired'
import { useLocalization } from '@/hooks/localization'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { useSelector } from 'react-redux'
import { useDispatch } from '@/hooks/dispatch'

const Messages = function Messages (props) {
  const scope = 'messages'
  const { localize, locale } = useLocalization()
  const isGranted = usePermissionChecker()
  const history = useHistory()
  const [activeTab, setActiveTab] = useState(props.match.params && props.match.params.tab && props.match.params.tab === MessagesTabs.DRAFT ? MessagesTabs.DRAFT : MessagesTabs.SENT)

  const [deleteMessage, sendMessage, clearDto, openDialog] = useDispatch([
    actionCreators.deleteMessage,
    actionCreators.sendMessage,
    actionCreators.clearDto,
    dialogCreators.openDialog
  ])
  const { lastReloadedAtDraft, lastReloadedAtSent, currentUser } = useSelector((state) => {
    const messages = state.messages
    const user = (state.identity && state.identity.user) ? state.identity.user.profile : null
    return {
      lastReloadedAtDraft: messages.lastReloadedAtDraft,
      lastReloadedAtSent: messages.lastReloadedAtSent,
      currentUser: user
    }
  })
  const { isReport } = props
  useEffect(clearDto, [])

  const canManage = isGranted('CanManageMessages')
  const canSend = isGranted('CanSendMessages')
  const canDraft = isGranted('CanCreateDrafts')

  const toggle = useCallback((tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab)
      history.push(`/${scope}/${tab}`)
    }
  }, [activeTab, history])

  const send = useCallback((dataItem) => {
    openDialog({
      dialogType: dialogTypes.confirm,
      title: 'Messages.Send',
      content: 'Messages.SendConfirm',
      primaryButtonText: 'Common.Confirm',
      onAction: () => sendMessage(dataItem.id)
    })
  }, [openDialog, sendMessage])

  const getButtons = useCallback(({ recipientType, ...dataItem }) => {
    let sourceActionButtons = []

    sourceActionButtons.push({
      url: x => `/${scope}/${activeTab}/${x.id}`,
      title: localize('Messages.Show'),
      icon: 'show'
    })

    if ((isReport && recipientType === 1) || (!isReport && recipientType === 0)) {
      if (canManage || canDraft) {
        sourceActionButtons.push({
          onClick: (dataItem) => history.push(`/${scope}/reply/${dataItem.id}`),
          title: localize('Messages.Reply'),
          icon: 'reply'
        })
      }
    }

    return sourceActionButtons
  }, [activeTab, canDraft, canManage, history, isReport, localize])

  const getDraftActionButtons = useCallback(({ creatorUserId, ...msgInfo }) => {
    const buttons = []

    if (canManage || (canDraft && creatorUserId && creatorUserId === (currentUser && parseInt(currentUser.id)))) {
      buttons.push({
        url: x => x.messageParentId ? `/${scope}/reply/${x.messageParentId}/${x.id}` : `/${scope}/${activeTab}/${x.id}`,
        title: localize('Messages.EditMessage'),
        icon: 'pencil'
      })
      buttons.push({ onDelete: (dataItem) => deleteMessage(dataItem.id), title: localize('Messages.DeteleMessage') })
    } else {
      buttons.push({
        url: (x) => x.messageParentId ? `/${scope}/reply/${x.messageParentId}/${x.id}` : `/${scope}/${activeTab}/${x.id}`,
        icon: 'show'
      })
    }
    if (canSend || (context.isInternalApp() && canManage)) { buttons.push({ onClick: send, title: localize('Messages.Send'), icon: 'send' }) }

    return buttons
  }, [activeTab, canDraft, canManage, canSend, currentUser, deleteMessage, localize, send])

  const getColumns = () => {
    const gridColumns = [
      NameColumn({ nameProp: 'senderName', latinNameProp: 'senderLatinName', locale, title: localize('Messages.Sender'), editable: false }),
      NameColumn({ nameProp: 'recipientName', latinNameProp: 'recipientLatinName', locale, title: localize('Messages.Recipient'), editable: false }),
      <Column field='subject' title={localize('Messages.Subject')} />,
      <Column field='sendingTime' title={localize('Messages.Sent')} filter='date' cell={DateTimeCell} filterCell={DateTimeFilterCell} />,
      <Column field='dueDate' title={localize('Messages.DueDate')} filter='date' cell={DateCell} filterCell={DateFilterCell} />,
      <Column title={localize('List.Detail')} {...actionButtonsColumnProps(getButtons)} />
    ]
    if (!isReport) { gridColumns.splice(4, 0, <Column field='readTime' title={localize('Messages.Read')} filter='date' cell={DateTimeCell} filterCell={DateTimeFilterCell} />) }
    return gridColumns
  }

  return (
    <Main>
      <MainTop>
        <MainTopTitle>
          <Resx name='Messages.Title' />
        </MainTopTitle>
        <MainTopLeft>
          <PermissionRequired name={['CanCreateDrafts', 'CanManageMessages']}>
            <Link className='btn btn-primary btn-sm' to={`/${scope}/${activeTab}/new`}>
              <Icon name='plus' />
              <span><Resx name='Messages.NewMessage' /></span>
            </Link>
          </PermissionRequired>
        </MainTopLeft>
      </MainTop>
      <MainBody transparent>
        <WhiteBox>
          <TabNavItems activeTabId={activeTab} onClick={activeTab => toggle(activeTab)} items={[
            { id: MessagesTabs.SENT, text: 'Messages.SentAndReceived', icon: 'message-rounded' },
            { id: MessagesTabs.DRAFT, text: 'Messages.Drafts', icon: 'menu-alt-left' }
          ]} />
          <TabContent activeTab={activeTab} renderActiveOnly>
            <TabPane tabId={MessagesTabs.SENT}>
              <BsrsGrid persistenceId={'messages'} dataSource={request => context.api.messagesService.getSentMessages(request)}
                defaultSort={[{ field: 'sendingTime', dir: 'desc' }]}
                lastReloadedAt={lastReloadedAtSent}>
                {getColumns().map((x, idx) => ({ ...x, key: idx }))}
              </BsrsGrid>
            </TabPane>
            <TabPane tabId={MessagesTabs.DRAFT}>
              <BsrsGrid persistenceId={'messages-draft'} dataSource={request => context.api.messagesService.getDraftMessages(request)} lastReloadedAt={lastReloadedAtDraft}>
                <Column title={localize('Messages.ParentMessage')} cell={(props) => <CustomCell {...props} onClick={(parentMessageId) => history.push(`/${scope}/${MessagesTabs.SENT}/${parentMessageId}`)} />} width={130} />
                {NameColumn({ nameProp: 'recipientName', latinNameProp: 'recipientLatinName', locale, title: localize('Messages.Recipient'), editable: false })}
                <Column field='subject' title={localize('Messages.Subject')} />
                <Column field='dueDate' title={localize('Messages.DueDate')} filter='date' cell={DateCell} filterCell={DateFilterCell} />
                <Column {...actionButtonsColumnProps((msgInfo) => getDraftActionButtons(msgInfo))} />
              </BsrsGrid>
            </TabPane>
          </TabContent>
        </WhiteBox>
      </MainBody>
    </Main>)
}

const CustomCell = function CustomCell (props) {
  const { dataItem } = props
  return (<td>
    {dataItem.messageParentId === null
      ? ''
      : <button className='btn-icon' onClick={() => props.onClick(dataItem.messageParentId)}><Icon
        name='show' /></button>}
  </td>)
}

export { Messages }



// WEBPACK FOOTER //
// ./src/components/messages/list.js